import { useCallback, useEffect, useRef, useState } from 'react';
import { WorkbookInstance } from '@fortune-sheet/react';
import type { Op } from '@fortune-sheet/core';
import { toast } from 'react-toastify';
import { downloadAsCsv, toCelldataSheet } from './converters';
import { downloadAsXlsx, parseSheetBytes } from './parseSheets';
import { useLazySheets } from './useLazySheets';
import { useResolveSheets, LoadingStage } from './useResolveSheets';
import { FortuneSheet } from './types';
import { getDocument, getDocumentHistory, saveDocument } from '../../store/investor/investorApiHelper';
import {
    isSheetS3StorageEnabled,
    isSheetStorageRef,
    getSheetDownloadUrl,
    hasVersionHistory,
    uploadSheetVersion,
    SheetStorageRef,
} from './sheetStorage';
import { useHistoryDrawer } from '../HistorySideDrawer/useHistoryDrawer';
import { HistoryEntry } from '../HistorySideDrawer/HistorySideDrawer';

export type SavingPhase = 'idle' | 'serializing' | 'uploading' | 'saving';
export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';
export type { LoadingStage };

// Debounce window after the last workbook edit before a DRAFT autosave fires.
// Longer than Plate's 1s (useDocumentAutosave.AUTOSAVE_DEBOUNCE_MS): a sheet
// autosave re-serializes the ENTIRE workbook to xlsx and re-uploads it (not an
// incremental JSON diff like Plate), which is heavy for large workbooks — a
// longer window keeps rapid edit bursts from re-triggering that work on every tick.
const SHEET_AUTOSAVE_DEBOUNCE_MS = 10_000;

// How long the header's autosave indicator stays on "Saved" before decaying
// back to idle. Unlike Plate's headerSyncStatus (always visible once seeded),
// this widget only lights up around an actual save, so it needs its own decay.
const AUTOSAVE_STATUS_DECAY_MS = 3000;

/**
 * A history preview mounts the Workbook read-only (allowEdit=false, pane open),
 * and Fortune-Sheet's read-only InputBox layout effect dispatches a context
 * update whose formula drain (groupValuesRefresh) rewrites `luckysheetfile` —
 * one of that effect's own dependencies. On a formula-heavy sheet the
 * execute→drain→re-fire chain exceeds React's nested-update limit at mount
 * ("Maximum update depth exceeded"). A committed version already carries every
 * computed value (`v`/`m`), so the preview COPY drops `f` and `calcChain` —
 * nothing left to execute, the drain never produces a patch, no loop. Only the
 * rendered preview is stripped: versionCacheRef keeps the full sheets, and
 * restore persists from those (plus storageOverride), at full fidelity.
 */
function stripFormulasForPreview(sheets: FortuneSheet[]): FortuneSheet[] {
    return sheets.map((sheet) => {
        const { calcChain, ...rest } = sheet;
        return {
            ...rest,
            celldata: sheet.celldata?.map((entry) => {
                if (!entry?.v || typeof entry.v !== 'object' || !('f' in entry.v)) return entry;
                const { f, ...cell } = entry.v;
                return { ...entry, v: cell };
            }),
        };
    });
}

export interface UseSheetEditorDrawerLogicArgs {
    open: boolean;
    fileName: string;
    /** In-memory sheets (import / composed). When provided, skips the URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — passed through to the save payload
     *  so a new version groups with its siblings (mirrors the Plate editor's
     *  persist path in ReportEditorRoute). */
    genId?: string;
    /** Save context; absent for the read-only viewer (Save disabled). */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    documentType?: string;
    name?: string;
    description?: string;
    /** S3 storage ref from a previously saved document — enables version continuity. */
    storage?: SheetStorageRef;
    /** The opened document's status ('DRAFT' | 'READY') — a draft-preferred open
     *  (getDocument by genId) can hand the drawer a live autosave draft, which
     *  Save & Close must still commit even when the session itself made no edits. */
    status?: string;
    /** Called with the saved document after a successful save. */
    onSaved?: (doc: any) => void;
    /** Called with the saved document after a name/description edit (the header's
     *  "Edit name & description" dialog) succeeds. Unlike `onSaved`, this does not
     *  imply the drawer should close — the caller only needs to refresh its own
     *  list view; the drawer already reflects the new name via `currentName`. */
    onRenamed?: (doc: any) => void;
}

export const EMPTY_SHEET: FortuneSheet = { name: 'Sheet1', id: '0', order: 0, status: 1, celldata: [], row: 50, column: 26 };

/**
 * Pick the sheet the user is actually viewing. Fortune-Sheet tracks the active
 * tab via `ctx.currentSheetId` (exposed as `getSheet().id`), NOT the per-sheet
 * `status` field — `status` is set once at load and never updated on tab switch,
 * so `status === 1` always resolves to sheet[0]. Match the live active id first,
 * then fall back to status, then sheet[0]. Exported for unit tests.
 */
export const pickActiveSheet = (sheets: FortuneSheet[], activeId?: string | null): FortuneSheet =>
    sheets.find((s) => String(s.id) === String(activeId)) ?? sheets.find((s) => Number(s.status) === 1) ?? sheets[0];

// Row/col/sheet structural changes (@fortune-sheet/core's patchToOp emits
// these with an empty path, identified by `op` alone) always carry real content.
const STRUCTURAL_OPS: ReadonlySet<Op['op']> = new Set(['insertRowCol', 'deleteRowCol', 'addSheet', 'deleteSheet']);
// Per-sheet path roots that hold real cell/sheet content or formatting, as
// opposed to Fortune-Sheet's activation/selection/scroll bookkeeping (tab
// switch, clicking around, scrolling).
const CONTENT_OP_PATH_ROOTS = new Set(['data', 'celldata', 'config', 'calcChain', 'row', 'column']);

/**
 * True when `op` represents content/format a save must capture. Verified
 * against @fortune-sheet/core's source (patchToOp, the Sheet/Context types,
 * and the sheet-tab-click handler):
 * - A real edit / paste / border / merge / column-resize mutates
 *   `luckysheetfile[i].<data|celldata|config|calcChain|row|column>` — patchToOp
 *   slices that down to `{ id: <sheetId>, path: ['data', ...], ... }`.
 * - Row/col insert-delete and add/delete-sheet are synthesised with an empty
 *   path but a literal `op` (`insertRowCol`/`deleteRowCol`/`addSheet`/`deleteSheet`).
 * - Tab switch, cell-click/selection, and scrolling only ever mutate TOP-LEVEL
 *   context fields (`currentSheetId`, `zoomRatio`, `sheetScrollRecord`,
 *   `luckysheet_select_save`, `luckysheet_selection_range`, `scrollLeft/Top`,
 *   `status`, …) — patchToOp never sets `id` for those, even when a field name
 *   collides with a per-sheet one (e.g. the tab-switch `zoomRatio` mirror), so
 *   requiring `id` here is what excludes them, not the specific path names.
 * Exported for reuse.
 */
export const isContentOp = (op: Op): boolean => {
    if (STRUCTURAL_OPS.has(op.op)) return true;
    if (!op.id) return false;
    return CONTENT_OP_PATH_ROOTS.has(String(op.path[0]));
};

/** True when at least one op in the batch is content (see isContentOp). */
export const hasContentOp = (ops: Op[]): boolean => ops.some(isContentOp);

// Stable reference — passed to useResolveSheets as an effect dependency, so it
// must not be reallocated per render. The editable drawer wants a usable blank
// grid when a fetched file parses to nothing.
const EMPTY_SHEET_FALLBACK: FortuneSheet[] = [EMPTY_SHEET];

/**
 * Owns the sheet-editor drawer's non-UI concerns: resolve sheets (in-memory or
 * fetched), track loading/error, and expose download + save handlers. Keeps
 * SheetEditorDrawer focused on composition/JSX.
 */
export const useSheetEditorDrawerLogic = ({
    open,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    documentType,
    name,
    description,
    storage: initialStorage,
    status: initialStatus,
    onSaved,
    onRenamed,
}: UseSheetEditorDrawerLogicArgs) => {
    // Resolve sheets (in-memory or fetch+parse) — shared with the read-only
    // SheetView via this hook, keeping the fetch/parse flow in one place.
    const { sheets, loading, loadingStage, error } = useResolveSheets({
        inMemorySheets,
        fileUrl,
        mimeType,
        fileName,
        open,
        emptyFallback: EMPTY_SHEET_FALLBACK,
    });
    const [saving, setSaving] = useState(false);
    const [savingPhase, setSavingPhase] = useState<SavingPhase>('idle');
    const workbookRef = useRef<WorkbookInstance>(null);

    // Track the S3 storage ref across saves within this session. Seeded from
    // the prop (opening an already-S3-backed doc), then latched after each save
    // — every save is an in-place PUT on the SAME S3 key, so this only ever
    // gains a fresh version number (same numeric docId) after the first save.
    // A REF like liveDocIdRef/liveGenIdRef, not state: it's read inside async
    // save closures, and a stale-closure read of undefined would re-mint a
    // second mnemosyne lineage for the same document.
    const storageRef = useRef<SheetStorageRef | undefined>(initialStorage);

    // True while the lineage's tip is an uncommitted draft this session must
    // still promote — seeded from the opened doc's status (a draft-preferred
    // open hands us a live draft), set by each DRAFT autosave, cleared by a
    // successful READY save. Lets Save & Close distinguish "nothing at all to
    // commit" (close silently) from "clean workbook but a pending draft".
    const hasUncommittedDraftRef = useRef(initialStatus === 'DRAFT');

    // The live doc/lineage identity — seeded from props, then latched after
    // the first save mints them (a fresh import/blank sheet has neither yet).
    // Mirrors ReportEditorRoute's liveDocIdRef so every save after the first
    // (autosave or explicit) updates the SAME row instead of forking a new one.
    const liveDocIdRef = useRef<string | undefined>(docId);
    const liveGenIdRef = useRef<string | undefined>(genId);

    // Single in-flight guard shared by autosave, Save & Close, and Rename — a
    // ref (not just the `saving` state) so a debounced autosave firing between
    // renders sees the latest value synchronously, with no stale-closure risk.
    const savingRef = useRef(false);
    // True from the last workbook edit until a save (autosave or explicit)
    // succeeds. Cleared ONLY on success — a failed autosave must retry.
    const dirtyRef = useRef(false);
    const autosaveTimerRef = useRef<number | null>(null);
    // Stable indirection so the debounce timer's setTimeout callback always
    // invokes the LATEST autosave closure (fresh saving/dirty/meta state),
    // never one captured stale at schedule time.
    const runAutosaveRef = useRef<() => void>(() => {});

    // Background autosave status — separate from `saving`/`savingPhase` (which
    // the Save & Close / Rename button reads) so a debounced autosave tick
    // never disables or relabels that button. Only runAutosave writes this.
    const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
    const autosaveStatusDecayRef = useRef<number | null>(null);

    // The currently-running save/rename/autosave operation (whichever holds
    // savingRef) — lets an explicit Save & Close or Rename that arrives while
    // an autosave tick is mid-flight AWAIT it and then proceed, instead of
    // silently no-oping and leaving the user's click unanswered.
    const inFlightSaveRef = useRef<Promise<any> | null>(null);

    // Own the current name/description here (seeded from props) rather than
    // reading the props directly in handleSave — a rename updates these in
    // place so a later plain Save persists the edited name, not the stale one
    // the drawer was opened with.
    const [currentName, setCurrentName] = useState(name);
    const [currentDescription, setCurrentDescription] = useState(description);

    // True while the version-history side pane is open — read via a ref (not
    // the closure) so markWorkbookDirty always sees the current value without
    // being re-created on every history toggle.
    const historyOpenRef = useRef(false);
    // A historical version currently shown read-only in place of the live
    // workbook. Kept entirely separate from `sheets`/`restoredSheets` — preview
    // content must never reach persistSheet or mark the workbook dirty.
    const [previewSheets, setPreviewSheets] = useState<FortuneSheet[] | null>(null);
    const [previewEntryId, setPreviewEntryId] = useState<string | null>(null);
    // Sheets from a successful restore — becomes the new "live" editable
    // content (the original `sheets` fetch is never mutated). Forces a
    // Workbook remount even when previewEntryId doesn't change (e.g. a direct
    // 3-dot restore with no prior preview).
    const [restoredSheets, setRestoredSheets] = useState<FortuneSheet[] | null>(null);
    const [restoreVersion, setRestoreVersion] = useState(0);
    const historyEntriesRef = useRef<HistoryEntry[]>([]);
    const closeHistoryRef = useRef<(() => void) | null>(null);
    // Cache of the CURRENTLY-viewed history version (sheets + its storage ref),
    // keyed by entry id. Bounded to one entry — selecting another version
    // evicts the old one, so browsing N versions of a large workbook never
    // holds N parsed copies in memory. Re-clicking the same row is still
    // instant. Cleared when the history pane closes (incl. after a restore).
    const versionCacheRef = useRef<Map<string, { sheets: FortuneSheet[]; storage?: SheetStorageRef }>>(new Map());

    // Set for the duration of each hydration updateSheet() call — see
    // useLazySheets' suppressDirtyRef and markWorkbookDirty below.
    const suppressDirtyRef = useRef(false);

    // Large multi-tab workbooks mount with only the active sheet hydrated —
    // stubs fill in right after first paint (see useLazySheets for the why).
    // A restored version takes over as the "live" sheets going forward.
    const effectiveSheets = restoredSheets ?? sheets;
    // Whichever content the <Workbook> currently mounts — a historical preview
    // (read-only, in place) or the live/editable sheets. Routed through the
    // SAME lazy-hydration machinery either way, so a large previewed version
    // mounts just as fast as the live workbook (a preview used to bypass
    // useLazySheets and mount every sheet eagerly).
    const displaySheets = previewSheets ?? effectiveSheets;
    const { workbookSheets, getFullSheets, hydratePending } = useLazySheets({
        sheets: displaySheets,
        workbookRef,
        suppressDirtyRef,
    });

    // Kick off stub hydration once the Workbook is mounted. Effects run after
    // the commit that mounts the child Workbook, so workbookRef is populated.
    useEffect(() => {
        if (!loading && !error && workbookSheets?.length) hydratePending();
    }, [loading, error, workbookSheets, hydratePending]);

    /** Latest sheet state: prefer the live workbook, fall back to resolved sheets.
     *  getFullSheets swaps never-activated stubs back for their full originals —
     *  without it, saving/downloading before touching every tab would wipe them. */
    const currentSheets = (): FortuneSheet[] => {
        const live = workbookRef.current?.getAllSheets();
        return live && live.length ? getFullSheets(live) : effectiveSheets ?? [];
    };

    const handleDownloadXlsx = async () => {
        const data = currentSheets();
        if (!data.length) return;
        await downloadAsXlsx(data, fileName);
        toast.success('Downloaded as .xlsx');
    };

    const handleDownloadCsv = () => {
        const data = currentSheets();
        if (!data.length) return;
        // Resolve against the live workbook's active sheet id (see pickActiveSheet);
        // getFullSheets keeps stub-safety by resolving that id in currentSheets().
        const active = pickActiveSheet(data, workbookRef.current?.getSheet()?.id);
        downloadAsCsv(active, fileName);
        toast.success(`Downloaded "${active.name}" as .csv`);
    };

    /**
     * Single save path shared by autosave (DRAFT), Save & Close, and Rename
     * (both READY). Uploads the current sheets (S3, or inline behind the
     * kill-switch) and persists the aica document row, addressed by whatever
     * doc/lineage identity is already known (a fresh doc omits both and lets
     * the backend mint them). `metaFields` carries name/description exactly as
     * the caller wants them spread — handleSave/autosave only send them when
     * truthy, Rename always sends both (including clearing to '') — so this
     * function never guesses at that policy itself.
     *
     * No inline fallback on S3 failure: a workbook large enough to need S3
     * would fail the same way against Mongo's 16 MB cap, just with a more
     * confusing error — so a failed upload fails the save outright instead of
     * silently retrying against a path that can't actually hold it.
     *
     * `silent` (background autosave) never touches `savingPhase` — that state
     * drives the Save & Close button's label, which must read its idle "Save
     * and Close" label throughout a background tick, not "Uploading…"/"Saving…".
     */
    const persistSheet = useCallback(
        async (
            status: 'DRAFT' | 'READY',
            metaFields: Record<string, unknown> = {},
            options: {
                /** Restore: omit `id` so the backend mints a new row in the SAME lineage (genId is kept). */
                fork?: boolean;
                isRestored?: boolean;
                /** Restore: the previewed version's sheets — already celldata-shaped
                 *  (read back from storage/history), never the live workbook's. */
                sheetsOverride?: FortuneSheet[];
                /** Restore: the previewed version's own storage ref. Its bytes already
                 *  exist as an immutable S3 object version, so the save just points the
                 *  new document row at it — no re-serialize, no re-upload. */
                storageOverride?: SheetStorageRef;
                /** Background autosave: never sets savingPhase (see the doc comment above). */
                silent?: boolean;
            } = {},
        ): Promise<any> => {
            const { fork, isRestored, sheetsOverride, storageOverride, silent } = options;
            const liveSheets = sheetsOverride ?? currentSheets();
            // Callers (handleSave/handleRename/runAutosave/handleRestoreVersion)
            // already gate on assesseeOrgId being present before ever calling persistSheet.
            const identityFields = {
                assesseeOrgId: assesseeOrgId as string,
                ...(!fork && liveDocIdRef.current ? { id: liveDocIdRef.current } : {}),
                ...(liveGenIdRef.current ? { genId: liveGenIdRef.current } : {}),
                ...(investorOrgId ? { investorOrgId } : {}),
                ...(createdBy ? { createdBy } : {}),
                ...(documentType ? { documentType } : {}),
                ...(isRestored ? { isRestored: true } : {}),
                ...metaFields,
            };

            let savedDoc: any;
            if (isSheetS3StorageEnabled()) {
                let ref = storageOverride;
                if (!ref) {
                    if (!silent) setSavingPhase('uploading');
                    // Live workbook sheets are RUNTIME shape (2D `data`) — serialize via
                    // the combined worker job (toCelldataSheet + buildXlsxBuffer in one
                    // round trip). A restore's sheetsOverride is already celldata, so it
                    // skips that conversion (runtimeSheets: false).
                    ref = await uploadSheetVersion({
                        sheets: liveSheets,
                        assesseeOrgId: assesseeOrgId as string,
                        investorOrgId,
                        existing: storageRef.current,
                        runtimeSheets: !sheetsOverride,
                        // Autosave re-points the lineage's live draft version in place
                        // (same versionNumber); Save & Close finalizes it — one history
                        // version per commit, mirroring the aica draft row it sits under.
                        isDraft: status === 'DRAFT',
                    });
                }
                storageRef.current = ref;

                if (!silent) setSavingPhase('saving');
                savedDoc = await saveDocument({ ...identityFields, editor: 'sheet', formatType: 'XLSX', status, storage: ref });
            } else {
                // Kill-switch path only (SHEET_S3_STORAGE_ENABLED === false). Small
                // sheets are cheap to walk on the main thread.
                const data = sheetsOverride ?? liveSheets.map(toCelldataSheet);
                savedDoc = await saveDocument({
                    ...identityFields,
                    editor: 'sheet',
                    formatType: 'XLSX',
                    status,
                    content: { version: 1, editor: 'sheet', value: data },
                });
            }

            const savedId = savedDoc?.id ?? savedDoc?._id;
            if (savedId) liveDocIdRef.current = savedId;
            if (savedDoc?.genId) liveGenIdRef.current = savedDoc.genId;
            // A DRAFT save leaves the lineage tip pending promotion; a READY save
            // (commit / rename / restore) promotes it — nothing left to commit.
            hasUncommittedDraftRef.current = status === 'DRAFT';
            return savedDoc;
        },
        [assesseeOrgId, investorOrgId, createdBy, documentType],
    );

    /** Returns true only on a successful save — the drawer closes on that signal. */
    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!assesseeOrgId) return false; // no save context (read-only viewer)
        // An autosave (or another save/rename) is mid-flight — wait for it
        // instead of silently no-oping this click, then proceed with our own save.
        if (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
        // Persist the celldata init form, not the runtime 2D `data` from
        // getAllSheets() — otherwise the saved doc reopens as an empty grid.
        if (!currentSheets().length) return false;
        // Nothing to commit: the doc already exists, nothing changed since its
        // last save, and no draft is pending promotion — close without minting a
        // pointless new version (or any network at all). A fresh never-saved
        // sheet (no liveDocId) still saves: Save & Close on it is explicit
        // create intent.
        if (!dirtyRef.current && !hasUncommittedDraftRef.current && liveDocIdRef.current) return true;
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        savingRef.current = true;
        setSaving(true);
        const op = (async (): Promise<boolean> => {
            try {
                const savedDoc = await persistSheet('READY', {
                    ...(currentName ? { name: currentName } : {}),
                    ...(currentDescription ? { description: currentDescription } : {}),
                });
                dirtyRef.current = false;
                toast.success('Saved');
                onSaved?.(savedDoc);
                return true;
            } catch (err) {
                console.error('[useSheetEditorDrawerLogic] save failed', err);
                toast.error('Failed to save');
                return false;
            } finally {
                setSaving(false);
                setSavingPhase('idle');
                savingRef.current = false;
                inFlightSaveRef.current = null;
            }
        })();
        inFlightSaveRef.current = op;
        return op;
    }, [assesseeOrgId, currentName, currentDescription, onSaved, persistSheet]);

    // Persists a name/description edit from the header's "Edit name & description"
    // dialog — the same saveDocument call handleSave makes, carrying the CURRENT
    // sheet content through (mirrors PlateDocumentDrawer's rename, which also
    // commits the live editor content alongside the metadata change).
    const handleRename = useCallback(
        async (newName: string, newDescription: string) => {
            if (!assesseeOrgId) return;
            // An autosave (or another save/rename) is mid-flight — wait for it
            // instead of silently no-oping this click, then proceed with the rename.
            if (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
            if (autosaveTimerRef.current) {
                window.clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
            // Rename commits the CURRENT content too (a full version on the S3
            // path) — so it shares the saving flag/phases with handleSave, both
            // for user feedback and to serialize against a concurrent Save.
            savingRef.current = true;
            setSaving(true);
            const op = (async () => {
                try {
                    const savedDoc = await persistSheet('READY', { name: newName, description: newDescription });
                    dirtyRef.current = false;
                    setCurrentName(newName);
                    setCurrentDescription(newDescription);
                    onRenamed?.(savedDoc);
                } catch (err) {
                    console.error('[useSheetEditorDrawerLogic] rename failed', err);
                    toast.error('Could not rename the report. Please try again.');
                } finally {
                    setSaving(false);
                    setSavingPhase('idle');
                    savingRef.current = false;
                    inFlightSaveRef.current = null;
                }
            })();
            inFlightSaveRef.current = op;
            return op;
        },
        [assesseeOrgId, onRenamed, persistSheet],
    );

    /**
     * DRAFT autosave tick — fires SHEET_AUTOSAVE_DEBOUNCE_MS after the last
     * workbook edit (see markWorkbookDirty). Shares the save path and the
     * single-flight guard with handleSave/handleRename so all three can never
     * run concurrently and a failed tick simply retries on the next edit.
     *
     * Non-blocking: sets ONLY `autosaveStatus`, never `saving`/`savingPhase` —
     * those drive the Save & Close button, which must stay enabled and
     * correctly labelled through a background autosave tick.
     */
    const runAutosave = useCallback(async () => {
        if (!assesseeOrgId) return; // no save context — nothing to autosave
        if (savingRef.current) {
            // Another save is in flight — reschedule instead of dropping the
            // pending edit; it'll run once that save finishes.
            autosaveTimerRef.current = window.setTimeout(() => runAutosaveRef.current(), SHEET_AUTOSAVE_DEBOUNCE_MS);
            return;
        }
        if (!dirtyRef.current) return; // nothing changed since the last successful save
        if (!currentSheets().length) return;

        savingRef.current = true;
        setAutosaveStatus('saving');
        const op = (async () => {
            try {
                await persistSheet(
                    'DRAFT',
                    {
                        ...(currentName ? { name: currentName } : {}),
                        ...(currentDescription ? { description: currentDescription } : {}),
                    },
                    { silent: true },
                );
                dirtyRef.current = false;
                setAutosaveStatus('saved');
                if (autosaveStatusDecayRef.current) window.clearTimeout(autosaveStatusDecayRef.current);
                autosaveStatusDecayRef.current = window.setTimeout(() => setAutosaveStatus('idle'), AUTOSAVE_STATUS_DECAY_MS);
            } catch (err) {
                // Never blocks editing or closes the drawer — the next edit's debounce
                // (or the flush-on-unmount below) retries with the latest content.
                console.warn('[useSheetEditorDrawerLogic] autosave failed, will retry', err);
                setAutosaveStatus('error');
            } finally {
                savingRef.current = false;
                inFlightSaveRef.current = null;
            }
        })();
        inFlightSaveRef.current = op;
        await op;
    }, [assesseeOrgId, currentName, currentDescription, persistSheet]);

    useEffect(() => {
        runAutosaveRef.current = runAutosave;
    }, [runAutosave]);

    /**
     * Pass to <Workbook onOp>: marks the workbook dirty and (re)schedules the
     * debounced autosave — but ONLY when `ops` actually contains real content
     * (see isContentOp). Fortune-Sheet's onOp fires for everything that goes
     * through its reducer, including tab switches, cell-click/selection, and
     * scrolling — none of those may arm the autosave timer.
     */
    const markWorkbookDirty = useCallback(
        (ops: Op[]) => {
            if (!assesseeOrgId) return; // read-only viewer — no autosave context
            if (historyOpenRef.current) return; // previewing a historical version is read-only — never autosaves it
            if (suppressDirtyRef.current) return; // hydration stub-fill in flight — path-identical to a real edit
            if (!hasContentOp(ops)) return;
            dirtyRef.current = true;
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = window.setTimeout(() => runAutosaveRef.current(), SHEET_AUTOSAVE_DEBOUNCE_MS);
        },
        [assesseeOrgId],
    );

    // Flush a still-pending dirty edit on unmount (drawer closed mid-debounce)
    // instead of silently dropping it — mirrors useDocumentAutosave's cleanup.
    useEffect(
        () => () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            if (autosaveStatusDecayRef.current) window.clearTimeout(autosaveStatusDecayRef.current);
            if (dirtyRef.current) runAutosaveRef.current();
        },
        [],
    );

    /**
     * Restore = persist the previewed version as a NEW committed version in the
     * SAME lineage (fork: omit `id`, keep `genId`) — mirrors
     * ReportEditorRoute.handleRestore. The version's bytes already live in S3
     * as an immutable object version, so `storageOverride` re-points the new
     * row at them — no re-serialize, no re-upload. Reuses persistSheet/
     * handleSave's saving/savingRef so it can never race a concurrent Save or
     * Rename.
     */
    const handleRestoreVersion = useCallback(
        async (sheetsToRestore: FortuneSheet[], storageOverride?: SheetStorageRef) => {
            if (!assesseeOrgId) return;
            if (!Array.isArray(sheetsToRestore) || !sheetsToRestore.length) return;
            if (savingRef.current && inFlightSaveRef.current) await inFlightSaveRef.current;
            savingRef.current = true;
            setSaving(true);
            const op = (async () => {
                try {
                    await persistSheet(
                        'READY',
                        {
                            ...(currentName ? { name: currentName } : {}),
                            ...(currentDescription ? { description: currentDescription } : {}),
                        },
                        { fork: true, isRestored: true, sheetsOverride: sheetsToRestore, storageOverride },
                    );
                    dirtyRef.current = false;
                    // The restored version takes over as the live editable content,
                    // and the history pane closes in the SAME update batch (its
                    // onDrawerClose clears preview state + the version cache), so
                    // the workbook remounts exactly once — EDITABLE. Remounting
                    // read-only (allowEdit=false, pane still open) is what crashed
                    // here: Fortune-Sheet's read-only InputBox layout effect and its
                    // groupValuesRefresh formula drain re-trigger each other past
                    // React's nested-update limit on formula-heavy workbooks.
                    setRestoredSheets(sheetsToRestore);
                    setRestoreVersion((v) => v + 1);
                    closeHistoryRef.current?.();
                    toast.success('Version restored');
                } catch (err) {
                    console.error('[useSheetEditorDrawerLogic] restore failed', err);
                    toast.error('Could not restore this version. Please try again.');
                } finally {
                    setSaving(false);
                    setSavingPhase('idle');
                    savingRef.current = false;
                    inFlightSaveRef.current = null;
                }
            })();
            inFlightSaveRef.current = op;
            await op;
        },
        [assesseeOrgId, currentName, currentDescription, persistSheet],
    );

    // Version history — reuses the shared HistorySideDrawer shell/UX (see
    // ReportEditorRoute for the reference wiring). Row click previews a version
    // in place (read-only, see previewSheets); the pane's footer Restore button
    // (restoreInFooter mode) → confirm modal → onSelectEntry restores it.
    const historyDrawer = useHistoryDrawer({
        documentType: 'REPORTS',
        fetchHistoryFn: async () => {
            const genId = liveGenIdRef.current;
            if (!genId || !assesseeOrgId) return [];
            const versions = await getDocumentHistory({ assesseeOrgId, genId });
            // Drafts are the working copy, never history.
            return versions
                .filter((v) => !v.isDraft)
                .map(
                    (v): HistoryEntry => ({
                        _id: String(v.id ?? v._id ?? ''),
                        userName: v.lastEditedBy ?? v.createdBy ?? '',
                        lastUpdatedAt: v.lastUpdatedAt ?? v.createdAt ?? 0,
                        isRestored: Boolean(v.isRestored),
                    }),
                );
        },
        fetchVersionFn: async (entryId: string) => {
            // A committed version's content never changes after creation, so
            // re-clicking the currently-viewed row renders instantly with zero
            // network. The cache is replaced (not grown) per entry — see
            // versionCacheRef.
            const cached = versionCacheRef.current.get(entryId);
            if (cached) return cached.sheets;

            const doc = (await getDocument({ docId: entryId, assesseeOrgId: assesseeOrgId as string })) as any;
            const ref: SheetStorageRef | undefined = isSheetStorageRef(doc?.storage) ? doc.storage : undefined;
            let versionSheets: FortuneSheet[];
            if (ref) {
                const url = await getSheetDownloadUrl(ref);
                const response = await fetch(url);
                const buffer = await response.arrayBuffer();
                versionSheets = await parseSheetBytes(buffer);
            } else {
                versionSheets = Array.isArray(doc?.content?.value) ? doc.content.value : [];
            }
            versionCacheRef.current = new Map([[entryId, { sheets: versionSheets, storage: ref }]]);
            return versionSheets;
        },
        onVersionLoaded: (data, shouldSave, entryId, isInitialLoad) => {
            if (shouldSave) {
                handleRestoreVersion(Array.isArray(data) ? data : [], entryId ? versionCacheRef.current.get(entryId)?.storage : undefined);
                return;
            }
            setPreviewEntryId(entryId ?? null);
            if (isInitialLoad) return; // the editor already shows the live/latest version
            const isLatest = historyEntriesRef.current[0]?._id === entryId;
            setPreviewSheets(isLatest ? null : stripFormulasForPreview(Array.isArray(data) ? data : []));
        },
        onDrawerClose: () => {
            // Return to the live editable content. No re-fetch needed — the live
            // sheets were never swapped out, only hidden behind the preview.
            setPreviewSheets(null);
            setPreviewEntryId(null);
            versionCacheRef.current.clear();
        },
    });

    useEffect(() => {
        historyOpenRef.current = historyDrawer.isOpen;
    }, [historyDrawer.isOpen]);
    useEffect(() => {
        historyEntriesRef.current = historyDrawer.historyEntries;
    }, [historyDrawer.historyEntries]);
    useEffect(() => {
        closeHistoryRef.current = historyDrawer.closeDrawer;
    }, [historyDrawer.closeDrawer]);

    return {
        sheets,
        /** What <Workbook data> should mount: the live sheets, or a historical
         *  preview's sheets — either way, full or active + stubs for large workbooks. */
        workbookSheets,
        loading,
        loadingStage,
        error,
        saving,
        savingPhase,
        /** Background autosave indicator — never gates the Save & Close button. */
        autosaveStatus,
        workbookRef,
        handleDownloadXlsx,
        handleDownloadCsv,
        handleSave,
        markWorkbookDirty,
        currentName,
        currentDescription,
        handleRename,
        /**
         * Gates the header's History button: a genId lineage must exist AND the
         * document's bytes must be individually addressable.
         *
         * On a bucket without object versioning a save overwrites the one object,
         * so every version of the lineage resolves to the SAME (current) bytes.
         * Listing them would offer the user versions that all render identically
         * — worse, previewing an old entry would silently show today's content.
         * A version-less storage ref is exactly that signal, so history stays
         * hidden and the document reads as the single current version it is.
         * Inline (non-S3) documents keep their history, which is Mongo-backed.
         */
        hasGenId: Boolean(liveGenIdRef.current) && (storageRef.current == null || hasVersionHistory(storageRef.current)),
        historyOpen: historyDrawer.isOpen,
        historyExpanded: historyDrawer.isExpanded,
        historyLoading: historyDrawer.loading,
        historyEntries: historyDrawer.historyEntries,
        historyPreviewLoading: historyDrawer.previewLoading,
        toggleHistoryDrawer: historyDrawer.toggleDrawer,
        closeHistoryDrawer: historyDrawer.closeDrawer,
        toggleHistoryExpand: historyDrawer.toggleExpand,
        /** Row click → in-place read-only preview. */
        previewHistoryEntry: historyDrawer.previewHistoryEntry,
        /** 3-dot menu confirm → restore (fork + persist). */
        selectHistoryEntry: historyDrawer.selectHistoryEntry,
        /** The history row currently selected/highlighted (incl. the auto-selected
         *  latest on pane open, which does NOT swap content). */
        previewEntryId,
        /** True only while a historical version's sheets are actually mounted in
         *  place of the live workbook — the only preview state that requires a
         *  Workbook remount. Row selection alone (previewEntryId) must not. */
        previewActive: Boolean(previewSheets),
        /** Bumped on every successful restore — fold into the Workbook `key` so it
         *  remounts even when previewEntryId doesn't change (a direct 3-dot restore). */
        restoreVersion,
    };
};
